import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Volet roulant électrique en panne' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Volet roulant électrique en panne : comment diagnostiquer',
  description: 'Diagnostic complet d\'une panne de volet roulant électrique : symptômes (ne réagit plus, bourdonne, s\'arrête), télécommande, condensateur, moteur. Toutes marques, Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-electrique-en-panne/',
  datePublished: '2026-03-30',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Mon volet roulant électrique ne réagit plus du tout, que vérifier en premier ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Vérifiez l\'alimentation électrique (disjoncteur), testez la télécommande (piles, voyant), et essayez l\'interrupteur mural si vous en avez un. Si rien ne fonctionne, le problème est probablement électrique (alimentation, carte électronique du moteur) et nécessite l\'intervention d\'un professionnel.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pourquoi mon volet électrique s\'arrête-t-il en cours de route ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Un arrêt en cours de montée ou de descente indique généralement un problème de fin de course (le volet pense être arrivé), une surchauffe du moteur (protection thermique), ou une lame du tablier qui coince dans les coulisses. Un diagnostic sur place permet de localiser précisément la cause.',
      },
    },
    {
      '@type': 'Question',
      name: 'Les moteurs de volet roulant sont-ils réparables ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Certains composants du moteur sont réparables, comme le condensateur de démarrage. En revanche, si le bobinage du moteur est grillé ou si la carte électronique est endommagée, le remplacement complet du moteur est généralement plus fiable et plus économique à long terme qu\'une réparation complexe.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-electrique-en-panne/', name: 'Volet roulant électrique en panne' } },
  ],
};

export default function ArticleVoletElectriquePanne() {
  return (
    <>
      <SEO
        heroImage="/images/blog-volet-electrique.png"
        title="Volet roulant électrique en panne : diagnostic"
        description="Volet roulant électrique en panne dans l'Hérault ? Diagnostic des symptômes : ne réagit plus, bourdonne, s'arrête. Télécommande, moteur, condensateur. DFPRO."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/volet-roulant-electrique-en-panne/"
      />
      <BlogArticleLayout
        title="Volet roulant électrique en panne : comment diagnostiquer"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="30 mars 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Le volet roulant électrique fait partie de ces équipements du quotidien dont on mesure l&apos;utilité quand ils tombent en panne. Confort, sécurité, isolation thermique : tout s&apos;arrête. L&apos;Hérault, avec ses étés chauds et ses hivers doux mais venteux, rend le volet roulant quasiment indispensable. Quand il tombe en panne, un diagnostic méthodique permet d&apos;identifier la cause avant de contacter un professionnel. Cet article vous aide à observer les symptômes et à communiquer efficacement avec votre réparateur.
        </p>

        <h2>Symptôme 1 : le volet ne réagit plus du tout</h2>
        <p>
          C&apos;est le cas le plus spectaculaire : vous appuyez sur l&apos;interrupteur, la télécommande, et il ne se passe absolument rien. Aucun bruit, aucun mouvement. Avant de paniquer, vérifiez l&apos;évidence : votre tableau électrique. Un disjoncteur qui a sauté est la cause la plus simple — et la plus rapide à résoudre. Essayez ensuite la télécommande : le voyant s&apos;allume-t-il quand vous appuyez ? Les piles sont-elles récentes ? Si vous avez un interrupteur mural en plus de la télécommande, testez-le : si l&apos;interrupteur fonctionne mais pas la télécommande, le problème vient du récepteur radio ou de la télécommande elle-même, pas du moteur.
        </p>
        <p>
          Si ni l&apos;interrupteur ni la télécommande ne produisent de réaction, et que le disjoncteur est en position normale, le problème est probablement électrique dans le coffre du volet : câble d&apos;alimentation débranché, carte électronique du moteur hors service, ou moteur complètement grillé. À ce stade, il faut faire appel à un professionnel pour un <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">diagnostic du moteur de volet roulant</Link>.
        </p>

        <h2>Symptôme 2 : le moteur bourdonne mais le volet ne bouge pas</h2>
        <p>
          Vous entendez un bourdonnement — le moteur est alimenté et tente de tourner — mais le tablier ne monte ni ne descend. C&apos;est le symptôme typique d&apos;un <strong>condensateur de démarrage défectueux</strong>. Le condensateur est un petit composant cylindrique logé à côté du moteur, dont le rôle est de fournir le « coup de boost » électrique nécessaire au démarrage de la rotation. Avec l&apos;âge et la chaleur — les coffres de volets exposés au soleil de l&apos;Hérault peuvent dépasser les 60°C en été — le condensateur perd sa capacité et ne peut plus lancer le moteur.
        </p>
        <p>
          Le remplacement du condensateur est une opération simple pour un technicien, mais elle requiert des précautions : un condensateur, même débranché, peut conserver une charge électrique dangereuse. <strong>Ne tentez pas cette réparation si vous n&apos;êtes pas formé.</strong> Nos techniciens DFPRO Hérault, qui interviennent dans tout le département, disposent de condensateurs compatibles avec les moteurs des principales marques et réalisent l&apos;opération en moins d&apos;une heure.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Volet électrique en panne dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            Diagnostic complet et <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">réparation de volet électrique</Link> sous 24 à 48h. Devis gratuit.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Symptôme 3 : le volet s&apos;arrête en cours de route</h2>
        <p>
          Le volet commence à monter ou descendre, puis s&apos;arrête brusquement sans raison apparente. Ce comportement intermittent peut avoir plusieurs causes. La première est un <strong>problème de fin de course</strong> : le dispositif qui indique au moteur la position du tablier est déréglé et envoie un signal d&apos;arrêt prématuré. La deuxième est une <strong>surchauffe du moteur</strong> : une protection thermique interne coupe l&apos;alimentation pour éviter la casse. Cela peut arriver si le tablier frotte anormalement dans ses coulisses — par exemple si une lame est tordue ou si les glissières sont encrassées — ce qui force le moteur à travailler plus dur.
        </p>
        <p>
          Enfin, un volet qui s&apos;arrête en cours de route peut signaler un <strong>problème de récepteur radio</strong> si vous utilisez une télécommande : des interférences, une pile faible, ou un récepteur vieillissant peuvent provoquer des coupures de signal et des arrêts intempestifs.
        </p>

        <h2>Symptôme 4 : la télécommande ne fonctionne plus</h2>
        <p>
          C&apos;est un cas fréquent qui n&apos;a rien à voir avec le moteur lui-même. Vérifiez d&apos;abord les piles de la télécommande — une pile usée est la cause numéro un. Si le voyant de la télécommande s&apos;allume mais que le volet ne réagit pas, le problème peut venir du récepteur radio logé dans le coffre. Il peut être déprogrammé — une micro-coupure de courant peut effacer la mémoire — ou tout simplement en panne. La reprogrammation d&apos;une télécommande de volet roulant suit une procédure spécifique à chaque marque (Somfy, Bubendorff, etc.) : un appui prolongé sur le bouton de programmation du récepteur, suivi d&apos;une pression sur la télécommande. Si vous n&apos;avez pas la notice, nos techniciens connaissent les procédures pour les principales marques et peuvent reprogrammer votre télécommande en quelques minutes.
        </p>

        <h2>Toutes marques de motorisation</h2>
        <p>
          Nous intervenons sur l&apos;ensemble des marques de motorisation de volets roulants présentes dans l&apos;Hérault : Somfy (la plus répandue), Bubendorff, Simu, Delta Dore, Nice, FAAC, et bien d&apos;autres. Chaque fabricant a ses spécificités — emplacement du condensateur, type de fins de course, protocole radio — et nos techniciens sont formés à ces différentes configurations. Nous disposons d&apos;un stock de condensateurs et de télécommandes couvrant les références les plus courantes, ce qui nous permet d&apos;intervenir rapidement, souvent en une seule visite.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Comment savoir si c&apos;est le moteur ou la télécommande qui est en panne ?</h3>
        <p>
          Le test le plus simple est d&apos;utiliser l&apos;interrupteur mural s&apos;il existe. Si le volet fonctionne avec l&apos;interrupteur mais pas avec la télécommande, le problème vient de la télécommande ou du récepteur radio. Si ni l&apos;un ni l&apos;autre ne fonctionnent, le problème est au niveau du moteur ou de son alimentation électrique.
        </p>

        <h3>Un moteur de volet roulant peut-il griller à cause d&apos;un orage ?</h3>
        <p>
          Oui, c&apos;est possible. Une surtension causée par la foudre peut endommager la carte électronique du moteur, surtout sur les modèles récents équipés de récepteurs radio intégrés. Si votre volet est tombé en panne juste après un orage, c&apos;est une piste sérieuse. Dans l&apos;Hérault, les orages d&apos;automne sont fréquents et peuvent provoquer ce type de dommage.
        </p>
      </BlogArticleLayout>
    </>
  );
}